import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { useDevice } from '../../constants/device/device';
import media from '../../constants/media';
import { IServiceImageCards } from '../../interfaces/umbracoNodes/contentSlices';
import { ContentContainer } from '../base/contentContainer/contentContainer';
import { Column, Columns } from '../base/grid';
import RichText from '../base/richText';
import RichTextTitle from '../base/richTextTitle';
import Button from '../button-v2';
import ImageCard from '../cards/imageCard';
import Slider from './serviceImageCardsSlider';

const Root = styled.div`
	margin-top: 150px;
`;

const RichTextTitleStyled = styled(RichTextTitle)<{ summaryPresent: boolean }>`
	${({ summaryPresent }) =>
		!summaryPresent &&
		css`
			${media.desktopSmall`
				text-align: center;
			`}
		`}
`;

const RichTextStyled = styled(RichText)`
	margin-top: 24px;

	${media.desktopSmall`
		margin-top: 0;
	`}
`;

const ButtonWrapper = styled.div`
	margin-top: 48px;
	display: flex;
`;

const TopColumnsStyled = styled(Columns)`
	margin-bottom: 0;
	width: calc(100% + 3rem);
`;

const TopColumnStyled = styled(Column)`
	margin-bottom: 0;
`;

const ColumnsStyled = styled(Columns)`
	justify-content: center;
	margin-top: 56px;
	width: calc(100% + 3rem);
`;

const ColumnStyled = styled(Column)`
	display: flex;
	margin-bottom: 3rem;
`;

const TitleAndLinkContainer = styled.div<{ orientVertically: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	margin-bottom: -3rem;

	${({ orientVertically }) =>
		orientVertically &&
		css`
			${media.desktopSmall`
				flex-direction: row;
				justify-content: space-between;

				${ColumnsStyled},
				${TopColumnsStyled} {
					margin-top: 0;
					width: calc(50% + 1.5rem);
				}

				${RichTextStyled} {
					margin-top: 24px;
				}
			`}
		`}
`;

interface Props {
	content: IServiceImageCards;
}

const ServiceImageCards: FC<Props> = ({ content }) => {
	const { smallDevice } = useDevice();

	return (
		<Root>
			<ContentContainer>
				<TitleAndLinkContainer orientVertically={!!content.Values.OrientVertically}>
					<TopColumnsStyled>
						<TopColumnStyled
							desktopSmall={!content.Values.Summary || content.Values.OrientVertically ? 1 : 1 / 2}>
							<RichTextTitleStyled
								content={content.Values.Title}
								type="h3"
								summaryPresent={!!content.Values.Summary}
							/>
						</TopColumnStyled>
						{content.Values.Summary && (
							<TopColumnStyled desktopSmall={content.Values.OrientVertically ? 1 : 1 / 2}>
								<RichTextStyled content={content.Values.Summary} />
								{content.Values.SummaryButton && (
									<ButtonWrapper>
										<Button
											title={content.Values.SummaryButton.Name}
											href={content.Values.SummaryButton.Url}
											target={content.Values.SummaryButton.Target}
										/>
									</ButtonWrapper>
								)}
							</TopColumnStyled>
						)}
					</TopColumnsStyled>
					{(!content.Values.DisplaySliderOnSmallDevices || !smallDevice) && (
						<ColumnsStyled>
							{content.Values.Cards.map((card, idx) => (
								<ColumnStyled
									key={idx}
									tablet={1 / 2}
									desktopSmall={content.Values.OrientVertically ? 1 : 1 / 2}>
									<ImageCard content={card} />
								</ColumnStyled>
							))}
						</ColumnsStyled>
					)}
				</TitleAndLinkContainer>
			</ContentContainer>
			{content.Values.DisplaySliderOnSmallDevices && smallDevice && <Slider cards={content.Values.Cards} />}
		</Root>
	);
};

export default ServiceImageCards;
